// ---------------------------------------------------------------------------
// Security, privacy and compliance content.
//
// This is the material enterprise fraud, risk and third party review teams ask
// for before they will sign. It is structured around the FTC Safeguards Rule
// (16 CFR Part 314), which is the rule our financial institution customers are
// examined against, and it is written so a reviewer can map our answers straight
// onto their questionnaire.
//
// RULES FOR EDITING THIS FILE
//  1. Never state a certification we do not hold. "Aligned to", "designed to
//     meet" and "in progress" are accurate; "certified" and "compliant" are
//     claims that must be backed by a report.
//  2. If a control changes in production, change it here the same week. A trust
//     page that does not match reality is worse than no trust page.
//  3. Anything marked TODO must be confirmed by legal or the Qualified
//     Individual before it is treated as a published commitment.
// ---------------------------------------------------------------------------

export interface AttestationStatus {
  icon: string;
  name: string;
  /** Short, honest status label. Never imply a certification we do not hold. */
  status: string;
  /** Drives the badge color: "active" for in place, "progress" for underway. */
  state: "active" | "progress";
  description: string;
}

export interface ControlArea {
  icon: string;
  title: string;
  description: string;
  /** Bullet level detail shown on the trust centre page. */
  details: string[];
}

export interface SafeguardMapping {
  /** Citation within 16 CFR Part 314. */
  citation: string;
  /** The safeguard the rule requires. */
  requirement: string;
  /** What VerifyDeceased actually does to meet it. */
  implementation: string;
}

export interface RetentionRule {
  dataType: string;
  retention: string;
  note: string;
}

export interface Subprocessor {
  purpose: string;
  /** TODO: replace with the named provider once legal approves public disclosure. */
  provider: string;
  dataHandled: string;
  location: string;
}

export interface RoadmapMilestone {
  phase: string;
  title: string;
  description: string;
  /** "done" renders as complete, "current" is highlighted, "next" is upcoming. */
  state: "done" | "current" | "next";
}

// ---------------------------------------------------------------------------
// Framework posture
// ---------------------------------------------------------------------------
export const ATTESTATIONS: AttestationStatus[] = [
  {
    icon: "shield",
    name: "GLBA Safeguards Rule",
    status: "Aligned",
    state: "active",
    description:
      "Our information security program is built against 16 CFR Part 314, including a designated Qualified Individual, a written risk assessment, access controls, encryption, monitoring and an annual review."
  },
  {
    icon: "award",
    name: "SOC 2 Type II",
    status: "In progress",
    state: "progress",
    description:
      "We are working toward a SOC 2 Type II report covering Security, Availability and Confidentiality. We do not hold a completed report yet and we will not claim one until we do. Ask us for the current timeline and the interim control summary."
  },
  {
    icon: "activity",
    name: "NIST CSF 2.0",
    status: "Mapped",
    state: "active",
    description:
      "Controls are mapped to the NIST Cybersecurity Framework 2.0 functions so reviewers who work in NIST terms can cross walk our answers without a translation exercise."
  },
  {
    icon: "check",
    name: "CIS Controls v8",
    status: "Mapped",
    state: "active",
    description:
      "System hardening and configuration baselines follow CIS Critical Security Controls v8 and vendor hardening guidance, with configuration drift checks on our production estate."
  }
];

// ---------------------------------------------------------------------------
// Headline control areas. Used on the home page and expanded on /security.
// ---------------------------------------------------------------------------
export const CONTROL_AREAS: ControlArea[] = [
  {
    icon: "lock",
    title: "Encryption in transit and at rest",
    description:
      "TLS 1.2 or higher on every connection and AES-256 encryption at rest across databases, object storage and backups.",
    details: [
      "TLS 1.2 minimum on all public endpoints, TLS 1.3 preferred, with weak ciphers disabled",
      "AES-256 encryption at rest for databases, uploaded files, result files and backups",
      "Encryption keys held in a managed key service with restricted access and rotation",
      "HTTPS enforced end to end, including the API and file download links"
    ]
  },
  {
    icon: "trash",
    title: "Short, documented data retention",
    description:
      "Records you submit are held only as long as needed to deliver and reconcile results, then deleted on a defined schedule.",
    details: [
      "Uploaded input files and result files are deleted on a defined schedule after delivery",
      "Customers can request earlier deletion of a specific batch at any time",
      "We do not sell, resell or license the records you submit to us",
      "Your submitted records are not used to train or enrich data sold to anyone else"
    ]
  },
  {
    icon: "key",
    title: "Access control and authentication",
    description:
      "Least privilege access, multi factor authentication on administrative access, and API keys scoped per environment.",
    details: [
      "Role based access aligned to job function, granted on a least privilege basis",
      "Multi factor authentication required for administrative and production access",
      "API keys are scoped per environment and can be rotated or revoked by the customer",
      "Joiner, mover and leaver process tied to HR events, with periodic access reviews"
    ]
  },
  {
    icon: "activity",
    title: "Logging, monitoring and testing",
    description:
      "Security relevant events are logged and monitored, with recurring vulnerability scanning and annual penetration testing.",
    details: [
      "Authentication, privileged action and data access events are logged and retained",
      "Recurring authenticated vulnerability scanning across application and infrastructure",
      "Annual penetration testing, with findings tracked to closure on a risk based timeline",
      "Patching and configuration baselines reviewed against CIS and vendor guidance"
    ]
  },
  {
    icon: "alert",
    title: "Incident response",
    description:
      "A documented incident response plan with defined severities, escalation paths and customer notification commitments.",
    details: [
      "Documented plan covering detection, containment, investigation, recovery and review",
      "Defined severity levels with named owners and escalation paths",
      "Customer notification without undue delay where your data is affected",
      "Post incident review feeds corrective actions back into the risk assessment"
    ]
  },
  {
    icon: "gavel",
    title: "Permissible use and privacy",
    description:
      "Death data is powerful. We contract for legitimate use only, and we contract our own providers to the same standard.",
    details: [
      "Customer agreements restrict use to legitimate purposes such as fraud prevention and claims",
      "Sources are licensed or accessed under the certification program that governs them",
      "Service providers are assessed before engagement and bound by written obligations",
      "Privacy policy and processing terms are available to review before you sign"
    ]
  }
];

// ---------------------------------------------------------------------------
// FTC Safeguards Rule mapping. This is the table reviewers screenshot.
// ---------------------------------------------------------------------------
export const SAFEGUARD_MAPPINGS: SafeguardMapping[] = [
  {
    citation: "§ 314.4(a)",
    requirement: "Designate a Qualified Individual to oversee the information security program",
    implementation:
      "A named Qualified Individual owns the program, reports to leadership on program status, risks, incidents and testing, and has the authority and budget to act."
  },
  {
    citation: "§ 314.4(b)",
    requirement: "Perform and document a written risk assessment",
    implementation:
      "A written risk assessment with a documented methodology covers internal and external threats, likelihood and impact, and control gaps. It is refreshed at least annually and after material change."
  },
  {
    citation: "§ 314.4(c)(1)",
    requirement: "Implement access controls and authentication",
    implementation:
      "Role based least privilege access, multi factor authentication on administrative and production access, environment scoped API keys, and periodic access reviews tied to HR events."
  },
  {
    citation: "§ 314.4(c)(2)",
    requirement: "Inventory data, systems and where customer information flows",
    implementation:
      "We maintain an inventory of the systems and stores that hold customer information, with data flow documentation covering upload, matching, delivery and deletion."
  },
  {
    citation: "§ 314.4(c)(3)",
    requirement: "Encrypt customer information in transit and at rest",
    implementation:
      "TLS 1.2 or higher in transit and AES-256 at rest across databases, uploaded files, result files and backups, with keys held in a managed key service."
  },
  {
    citation: "§ 314.4(c)(4)",
    requirement: "Adopt secure development practices and change management",
    implementation:
      "Peer reviewed changes, dependency and static analysis in the pipeline, hardened configuration baselines, and change records for production deployments."
  },
  {
    citation: "§ 314.4(c)(5)",
    requirement: "Monitor and log authorised user activity and detect unauthorised access",
    implementation:
      "Security relevant events including authentication, privileged actions and access to customer information are logged, retained and monitored for anomalies."
  },
  {
    citation: "§ 314.4(c)(6)",
    requirement: "Maintain secure disposal and business continuity for customer information",
    implementation:
      "Encrypted backups with restoration testing, defined recovery objectives, and scheduled secure deletion of submitted records once they are no longer needed."
  },
  {
    citation: "§ 314.4(d)",
    requirement: "Test and monitor the effectiveness of safeguards",
    implementation:
      "Recurring authenticated vulnerability scanning and annual penetration testing, with findings tracked to closure and fed back into the risk assessment."
  },
  {
    citation: "§ 314.4(e)",
    requirement: "Train staff and provide security awareness",
    implementation:
      "Security awareness training at onboarding and annually, role specific training for engineering and support, and phishing simulation with follow up."
  },
  {
    citation: "§ 314.4(f)",
    requirement: "Oversee service providers",
    implementation:
      "Providers are assessed before engagement, bound by written security obligations including breach notification, and reassessed on a risk based cadence."
  },
  {
    citation: "§ 314.4(h)",
    requirement: "Maintain a written incident response plan",
    implementation:
      "A documented plan covering detection, containment, investigation, notification and post incident review, exercised at least annually."
  },
  {
    citation: "§ 314.4(i)",
    requirement: "Report to the board or senior leadership at least annually",
    implementation:
      "The Qualified Individual delivers a written annual report covering program status, risk assessment results, testing outcomes, incidents and recommended improvements."
  }
];

// ---------------------------------------------------------------------------
// Retention schedule
// TODO: confirm each window with the Qualified Individual before treating these
// as contractual commitments. They must match what the platform actually does.
// ---------------------------------------------------------------------------
export const RETENTION_SCHEDULE: RetentionRule[] = [
  {
    dataType: "Uploaded input files",
    retention: "Deleted on a defined schedule after results are delivered",
    note: "Earlier deletion available on request for any specific batch."
  },
  {
    dataType: "Verification result files",
    retention: "Available for download for a limited window, then deleted",
    note: "Download and keep your results inside your own retention policy."
  },
  {
    dataType: "Match and billing metadata",
    retention: "Retained for the life of the account plus statutory record keeping",
    note: "Record counts and charges only. No underlying identity fields."
  },
  {
    dataType: "Account and contact information",
    retention: "Retained for the life of the account",
    note: "Deleted on request after account closure, subject to legal obligations."
  },
  {
    dataType: "Security and audit logs",
    retention: "Retained for a defined period to support investigation",
    note: "Required to meet monitoring and incident response obligations."
  },
  {
    dataType: "Encrypted backups",
    retention: "Rotated out on the backup cycle",
    note: "Backups are encrypted and restoration is tested."
  }
];

// ---------------------------------------------------------------------------
// Subprocessors
// TODO: legal to approve naming each provider publicly. Until then reviewers get
// the named list inside the security package under NDA.
// ---------------------------------------------------------------------------
export const SUBPROCESSORS: Subprocessor[] = [
  {
    purpose: "Cloud infrastructure and hosting",
    provider: "Named in the security package",
    dataHandled: "Uploaded records, result files, application data",
    location: "United States"
  },
  {
    purpose: "Payment processing",
    provider: "Named in the security package",
    dataHandled: "Billing contact and card data, handled by the processor",
    location: "United States"
  },
  {
    purpose: "Transactional email",
    provider: "Named in the security package",
    dataHandled: "Account email address and notification content",
    location: "United States"
  },
  {
    purpose: "Error monitoring and observability",
    provider: "Named in the security package",
    dataHandled: "Operational telemetry and diagnostic metadata",
    location: "United States"
  }
];

// ---------------------------------------------------------------------------
// SOC 2 readiness roadmap.
//
// Enterprise buyers do not need us to already hold the report. They need to see
// that the work is real, sequenced and owned. Move an entry to "done" only when
// the underlying work is genuinely finished, and keep exactly one entry marked
// "current" so the page never looks stale.
// ---------------------------------------------------------------------------
export const SOC2_ROADMAP: RoadmapMilestone[] = [
  {
    phase: "Phase 1",
    title: "Security program documented",
    description:
      "Written information security program built against 16 CFR Part 314, with a designated Qualified Individual, a documented risk assessment and defined control ownership.",
    state: "done"
  },
  {
    phase: "Phase 2",
    title: "Controls implemented and evidenced",
    description:
      "Encryption, access control, logging, change management, retention and incident response operating in production with the evidence trail an auditor needs to sample.",
    state: "done"
  },
  {
    phase: "Phase 3",
    title: "Readiness assessment and gap remediation",
    description:
      "Independent review against the Security, Availability and Confidentiality criteria, with identified gaps tracked to closure on a risk based timeline.",
    state: "current"
  },
  {
    phase: "Phase 4",
    title: "Type I report",
    description:
      "Point in time attestation that our controls are suitably designed. This is the first artefact we will be able to hand to a reviewer.",
    state: "next"
  },
  {
    phase: "Phase 5",
    title: "Type II report",
    description:
      "Attestation over an observation window that the controls operated effectively, covering Security, Availability and Confidentiality.",
    state: "next"
  }
];

/** What a reviewer receives when they ask for our security package. */
export const SECURITY_PACKAGE_ITEMS: string[] = [
  "Completed security questionnaire, or your own questionnaire returned",
  "FTC Safeguards Rule (16 CFR Part 314) control mapping",
  "Encryption, key management and data retention detail",
  "Incident response and breach notification commitments",
  "Business continuity, backup and restoration testing summary",
  "Named subprocessor list and their security obligations",
  "Most recent penetration test summary and remediation status",
  "SOC 2 Type II readiness status and current timeline"
];

export interface TrustBadge {
  icon: string;
  title: string;
  detail: string;
}

/**
 * The credential strip in the site footer.
 *
 * Every entry must be a fact we can evidence if a buyer asks. No third party
 * seals (BBB, Trustpilot, TRUSTe) until we genuinely hold them, because a fake
 * seal is the fastest way to fail an enterprise review.
 */
export const FOOTER_TRUST: TrustBadge[] = [
  {
    icon: "landmark",
    title: "US owned and operated",
    detail: "California headquarters, published address and a phone number that a person answers."
  },
  {
    icon: "gavel",
    title: "GLBA Safeguards aligned",
    detail: "Information security program built against FTC 16 CFR Part 314."
  },
  {
    icon: "lock",
    title: "Encrypted end to end",
    detail: "TLS 1.2 or higher in transit and AES-256 at rest, including backups."
  },
  {
    icon: "server",
    title: "US data residency",
    detail: "Hosting, processing and backups stay inside United States regions."
  },
  {
    icon: "trash",
    title: "Your records deleted",
    detail: "Files you upload are deleted on a published schedule and never resold."
  },
  {
    icon: "document",
    title: "Permissible use enforced",
    detail: "Contracts limit use to fraud prevention, claims and other lawful purposes."
  }
];

export const COMPLIANCE_FAQS = [
  {
    question: "Are you SOC 2 certified?",
    answer:
      "Not yet, and we will not say otherwise. We are working toward a SOC 2 Type II report covering Security, Availability and Confidentiality, and we are happy to share the current timeline and our interim control summary. In the meantime our information security program is built and documented against the FTC Safeguards Rule, which is the standard our financial institution customers are examined against."
  },
  {
    question: "How do you support GLBA Safeguards Rule obligations?",
    answer:
      "Our program follows 16 CFR Part 314. We have a designated Qualified Individual, a written risk assessment refreshed at least annually and after material change, access controls with multi factor authentication, encryption in transit and at rest, logging and monitoring, secure development and change management, service provider oversight, a documented incident response plan, and an annual written report to leadership. We publish the full control mapping on our security page."
  },
  {
    question: "How is our data encrypted?",
    answer:
      "Every connection uses TLS 1.2 or higher, with TLS 1.3 preferred and weak ciphers disabled. Data at rest is encrypted with AES-256 across databases, uploaded files, result files and backups. Encryption keys are held in a managed key service with restricted access and rotation."
  },
  {
    question: "How long do you keep the records we submit?",
    answer:
      "Only as long as needed to deliver and reconcile results. Uploaded input files and result files are deleted on a defined schedule, and you can request earlier deletion of a specific batch at any time. We do not sell, resell or license the records you send us, and we do not use them to enrich data we sell to anyone else."
  },
  {
    question: "Will you complete our vendor security questionnaire?",
    answer:
      "Yes. Our team completes customer questionnaires and joins vendor review calls as a standard part of onboarding. Ask for the security package and you will receive our control mapping, encryption and retention detail, incident response commitments, business continuity summary, subprocessor list and the most recent penetration test summary."
  },
  {
    question: "What happens if there is a security incident?",
    answer:
      "We maintain a documented incident response plan with defined severity levels, named owners and escalation paths. If your data is affected we notify you without undue delay with what we know, what we are doing and what we need from you. Every incident is followed by a post incident review that feeds corrective actions back into our risk assessment."
  },
  {
    question: "Where is our data hosted?",
    answer:
      "In the United States. Cloud infrastructure, backups and processing all stay within US regions. The named subprocessor list, including hosting, payments, email and observability, is provided in the security package."
  },
  {
    question: "Can we restrict how results are used inside our organization?",
    answer:
      "Yes, and we encourage it. API keys are scoped per environment and can be rotated or revoked by you at any time, and access to results in the dashboard is controlled per account. Our terms also restrict use of results to legitimate purposes such as fraud prevention, claims validation and program integrity."
  }
];
